import {_utils, viewportChecker} from "../../utils/_utils.js";
import {_libs} from "../../utils/_libs.js";
import {_state} from "../../state/state.js";
import {watchPageHeight} from "../../utils/scroll-refresh.js";
import {EffectFade} from "swiper/modules";

/**
 * Scroll tabs (FBO Services, TZ section 4). One slide per tab (title, photo, text).
 * - Desktop: slides are stacked; the body is position: sticky inside a taller stage (CSS, 1 screen per tab) and scroll
 *   progress picks the active slide → photo crossfade, title + content fade, progress segments fill up to the current
 *   one; reverse on scroll up. Clicking a segment scrolls to its slice of the stage.
 * - Mobile/tablet (user): Swiper with loop + fade effect (crossfade, swipe/drag switches); segments follow the real index and sit under
 *   the current photo (titles differ in height → positioned in JS); tap a segment = go to that tab.
 * Swiper / ScrollTrigger are (re)created only when crossing the desktop breakpoint.
 */
export const scrollTabs = () => {
    return new Promise((resolve) => {
        _utils.initSection('.scroll-tabs', (section) => init(section));
        watchPageHeight();
        resolve();
    });
};

const init = (section) => {
    const body = section.querySelector('.scroll-tabs__body');
    const stage = section.querySelector('.scroll-tabs__stage');
    const sticky = section.querySelector('.scroll-tabs__sticky');
    const slider = section.querySelector('.scroll-tabs__slider');
    const progress = section.querySelector('.scroll-tabs__progress');
    const slides = [...section.querySelectorAll('.scroll-tabs__slide')];
    const segments = [...section.querySelectorAll('.scroll-tabs__segment')];
    if (!stage || !sticky || !slider || slides.length < 2) return;

    const count = slides.length;
    let current = 0;
    let tl = null;
    let swiper = null;

    const updateSegments = (index) => {
        section.style.setProperty('--st-active', String(index));
        segments.forEach((segment, i) => {
            segment.classList.toggle('is-done', i <= index);
            segment.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
    };

    // desktop: stacked slides
    const setActive = (index) => {
        index = Math.max(0, Math.min(count - 1, index));
        if (index === current) return;
        current = index;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        updateSegments(index);
    };

    // mobile: segments under the photo of the visible slide
    const placeProgress = () => {
        if (!swiper || !progress) return;
        const slide = swiper.slides[swiper.activeIndex];
        const image = slide?.querySelector('.scroll-tabs__image');
        if (!image) return;
        const top = image.getBoundingClientRect().bottom - body.getBoundingClientRect().top + 11; // line at +14 (10px tap padding)
        body.style.setProperty('--st-progress-top', `${top}px`);
    };

    const stickyTop = () => parseFloat(getComputedStyle(sticky).top) || 0;

    const build = () => {
        if (tl !== null) {
            tl.scrollTrigger && tl.scrollTrigger.kill(true);
            tl.kill();
            tl = null;
        }
        if (swiper !== null) {
            swiper.destroy(true, true);
            swiper = null;
            body.style.removeProperty('--st-progress-top');
        }

        if (_state.isDesktop) {
            slides.forEach((slide, i) => slide.classList.toggle('is-active', i === current));
            updateSegments(current);

            // from the moment the body sticks until it un-sticks (stage bottom = sticky bottom)
            tl = _libs._gsap({
                trigger: stage,
                start: () => `top ${stickyTop()}px`,
                end: () => `bottom ${stickyTop() + sticky.offsetHeight}px`,
                invalidateOnRefresh: true,
                //markers: true,
                onUpdate: (self) => {
                    setActive(Math.min(count - 1, Math.floor(self.progress * count)));
                },
            });
        } else {
            swiper = _libs._swiper(slider, {
                modules: [EffectFade],
                effect: 'fade', // crossfade like the desktop (user); swipe still switches the slide
                fadeEffect: {crossFade: true},
                loop: true,
                slidesPerView: 1,
                speed: 700,
                grabCursor: true,
                initialSlide: current,
                on: {
                    init: (sw) => {
                        current = sw.realIndex;
                        updateSegments(current);
                        requestAnimationFrame(placeProgress);
                    },
                    slideChange: (sw) => {
                        current = sw.realIndex;
                        updateSegments(current);
                        placeProgress();
                    },
                    resize: placeProgress,
                },
            });
            placeProgress();
        }
    };

    build();

    // segments: desktop → scroll to the tab's slice; mobile → go to the slide
    segments.forEach((segment, index) => {
        segment.addEventListener('click', () => {
            if (_state.isDesktop && tl?.scrollTrigger) {
                const st = tl.scrollTrigger;
                const target = st.start + (st.end - st.start) * ((index + 0.5) / count);
                if (_state.lenisInit !== null) {
                    _state.lenisInit.scrollTo(target, {duration: 1});
                } else {
                    window.scrollTo({top: target, behavior: 'smooth'});
                }
            } else {
                swiper?.slideToLoop(index);
            }
        });
    });

    // images load lazily → the photo height (and the segments' position) can change
    slider.querySelectorAll('img').forEach((img) => img.addEventListener('load', placeProgress, {once: true}));

    let wasDesktop = _state.isDesktop;
    _utils.rebuildComponentsOnResize(() => {
        viewportChecker.all(); // updates _state.isDesktop
        if (_state.isDesktop === wasDesktop) {
            placeProgress();
            return;
        }
        wasDesktop = _state.isDesktop;
        build();
    });
};
